"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { BuddyOverlay, type BuddyBeat } from "./BuddyOverlay";
import { useClientBuddyScript } from "./useClientBuddyScript";
import type { ShowcaseTab } from "@/components/feed-header";
import {
  buildAutomationsTourUrl,
  buildBlogTourUrl,
  buildCommunityTourUrl,
  consumeOnboardingResumeStage,
  getHomeShowcaseStage,
  HOME_SHOWCASE_TOUR_QUERY_PARAM,
  HOME_SHOWCASE_TOUR_TAB_EVENT,
  type HomeShowcaseStage,
} from "@/lib/onboarding-constants";

// Maps beat IDs to which showcase tab the home feed should be on
// while that beat is active. The tour tab event flips the underlying
// `<Feed>` component to the right tab so the spotlighted card row is
// actually visible behind the bubble.
//
// For trade_ideas / community resume stages the FIRST beat ("click
// the pill yourself") explicitly maps to "automations" because the
// whole point of the beat is for the user to click the inactive
// pill themselves. The follow-up "browse more" beat then maps to
// the corresponding tab so the right cards are visible.
const BEAT_TAB: Record<string, ShowcaseTab> = {
  home_feed_intro: "automations",
  home_automations_browse_more: "automations",
  home_trade_ideas_pill_click: "automations",
  home_trade_ideas_browse_more: "curated",
  home_community_pill_click: "automations",
  home_community_browse_more: "community",
};

// Each stage's leading tab. For automations we force the tab on
// stage start so the alert cards are immediately visible behind the
// intro bubble. For trade_ideas / community we leave it null so the
// home loads on its default automations tab and the user is forced
// to click the pill themselves (per product feedback: feels more
// like a guided demo, less like a railroad).
const STAGE_TAB: Record<HomeShowcaseStage, ShowcaseTab | null> = {
  automations: "automations",
  trade_ideas: null,
  community: null,
  finale: null,
};

function emitTourTab(tab: ShowcaseTab | null) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(HOME_SHOWCASE_TOUR_TAB_EVENT, { detail: { tab } }),
  );
}

/**
 * HomeShowcaseBuddyTour
 *
 * Drives the home-page leg of the cross-page onboarding chain. The
 * tour is staged: the same component renders different beats based
 * on the `?onboarding_home=` value, and each stage hands off to a
 * different downstream page tour.
 *
 * Stages:
 *   - "automations"  → existing intro + click "Browse more" on the
 *                       automations pill row → /automations tour.
 *   - "trade_ideas"  → flips the feed tab to "Top Trade Ideas",
 *                       prompts the user to click Browse more →
 *                       /blog tour.
 *   - "community"    → flips the feed tab to "Community Use Cases",
 *                       prompts the user to click Browse more →
 *                       /community tour.
 *   - "finale"       → no beats here; an OnboardingFinaleModal mounts
 *                       elsewhere and reads the same param.
 *
 * Cross-page handoff: the upstream pages (alert public tour, blog
 * article tour, community chat tour) all complete by clicking the
 * sidebar Xynth logo. That click navigates to "/" via the natural
 * Link in `app-sidebar.tsx`, which strips query params. To survive
 * that strip, the upstream tour writes the next stage to
 * sessionStorage via `setOnboardingResumeStage`. On mount we
 * consume that flag and `router.replace` the URL with the matching
 * `?onboarding_home=<stage>` so the right beats fire and a
 * subsequent refresh keeps the user in the same stage.
 */
export function HomeShowcaseBuddyTour() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const startedRef = useRef(false);
  const resumeAppliedRef = useRef(false);

  // Stage detection: prefer URL, but on mount also consume any
  // pending sessionStorage resume flag and reflect it back into the
  // URL so the rest of this component's logic only has to look at
  // the URL state.
  const urlStage = getHomeShowcaseStage(searchParams);
  const [resumeStage, setResumeStage] = useState<HomeShowcaseStage | null>(
    null,
  );

  useEffect(() => {
    if (resumeAppliedRef.current) return;
    if (urlStage) return; // URL already encodes a stage; no resume needed.
    const pending = consumeOnboardingResumeStage();
    if (!pending) return;
    resumeAppliedRef.current = true;
    setResumeStage(pending);
    try {
      const next = new URLSearchParams(searchParams?.toString() || "");
      next.set(
        HOME_SHOWCASE_TOUR_QUERY_PARAM,
        pending === "automations" ? "1" : pending,
      );
      const search = next.toString();
      router.replace(`/${search ? `?${search}` : ""}`, { scroll: false });
    } catch {
      /* If URL replace fails the local resumeStage state still drives
         the tour — refresh would lose position, but that's acceptable. */
    }
  }, [router, searchParams, urlStage]);

  const stage: HomeShowcaseStage | null = urlStage ?? resumeStage;
  const shouldRun = stage !== null && stage !== "finale";

  const beats = useMemo<BuddyBeat[]>(() => {
    if (!shouldRun || !stage) return [];
    if (stage === "automations") {
      return [
        {
          beatId: "home_feed_intro",
          trigger: { type: "client" },
          copy: {
            title: "Premium resources",
            body:
              "This is where you'll find premium resources designed to guide you on your trading journey, including premade signals, trading guides, and public chats from other Xynth users.",
            cta: "Next",
          },
          anchor: { kind: "css", value: "[data-tour='home-content-feed']" },
          hideSpotlightRing: true,
          bubblePlacement: "anchorAbove",
          bubbleOffset: { y: -36 },
        },
        {
          beatId: "home_automations_browse_more",
          trigger: { type: "client" },
          copy: {
            title: "Automations",
            body:
              "First up, automations. Premade live signals and alert pipelines that fire on your schedule. Tap Browse more to open the full library.",
          },
          anchor: { kind: "css", value: "[data-tour='home-automations-pill']" },
          actionTarget: {
            kind: "css",
            value: "[data-tour='home-automations-browse-more']",
          },
          action: { kind: "click_target" },
          spotlight: { kind: "css", value: "[data-tour='home-content-feed']" },
          bubbleAnchor: { kind: "css", value: "[data-tour='home-content-feed']" },
          hideSpotlightRing: true,
          bubblePlacement: "anchorAbove",
          bubbleOffset: { y: -56 },
        },
      ];
    }
    if (stage === "trade_ideas") {
      return [
        {
          beatId: "home_trade_ideas_pill_click",
          trigger: { type: "client" },
          copy: {
            title: "Trade Ideas",
            body:
              "Tap the Top Trade Ideas pill to explore data-backed setups from Xynth users.",
          },
          anchor: { kind: "css", value: "[data-tour='home-trade-ideas-pill']" },
          action: { kind: "click_target" },
          spotlight: { kind: "css", value: "[data-tour='home-content-feed']" },
          hideSpotlightRing: true,
          // The home page is already at the top from the resume
          // navigation, so suppress the auto-scroll.
          noScroll: true,
          bubblePlacement: "anchorAbove",
          bubbleOffset: { y: -16 },
        },
        {
          beatId: "home_trade_ideas_browse_more",
          trigger: { type: "client" },
          copy: {
            title: "See every play",
            body:
              "Click Browse more to see more Xynth-ranked trade setups.",
          },
          anchor: { kind: "css", value: "[data-tour='home-trade-ideas-pill']" },
          actionTarget: {
            kind: "css",
            value: "[data-tour='home-automations-browse-more']",
          },
          action: { kind: "click_target" },
          spotlight: { kind: "css", value: "[data-tour='home-content-feed']" },
          hideSpotlightRing: true,
          bubblePlacement: "anchorAbove",
          bubbleOffset: { y: -16 },
        },
      ];
    }
    if (stage === "community") {
      return [
        {
          beatId: "home_community_pill_click",
          trigger: { type: "client" },
          copy: {
            title: "Community",
            body:
              "Shared research sessions from Xynth users. Tap Community Use Cases to view them.",
          },
          anchor: { kind: "css", value: "[data-tour='home-community-pill']" },
          action: { kind: "click_target" },
          spotlight: { kind: "css", value: "[data-tour='home-content-feed']" },
          hideSpotlightRing: true,
          noScroll: true,
          bubblePlacement: "anchorAbove",
          bubbleOffset: { y: -16 },
        },
        {
          beatId: "home_community_browse_more",
          trigger: { type: "client" },
          copy: {
            title: "Open the library",
            body:
              "Browse the full set of shared research sessions.",
          },
          anchor: { kind: "css", value: "[data-tour='home-community-pill']" },
          actionTarget: {
            kind: "css",
            value: "[data-tour='home-automations-browse-more']",
          },
          action: { kind: "click_target" },
          spotlight: { kind: "css", value: "[data-tour='home-content-feed']" },
          hideSpotlightRing: true,
          bubblePlacement: "anchorAbove",
          bubbleOffset: { y: -16 },
        },
      ];
    }
    return [];
  }, [shouldRun, stage]);

  const stripQueryParam = useMemo(
    () => () => {
      try {
        const next = new URLSearchParams(searchParams?.toString() || "");
        next.delete(HOME_SHOWCASE_TOUR_QUERY_PARAM);
        const search = next.toString();
        const path =
          window.location.pathname + (search ? `?${search}` : "");
        router.replace(path, { scroll: false });
      } catch {
        /* URL mutation is best-effort. */
      }
    },
    [router, searchParams],
  );

  const advanceStage = useMemo(
    () => () => {
      emitTourTab(null);
      if (stage === "automations") {
        router.push(buildAutomationsTourUrl());
      } else if (stage === "trade_ideas") {
        router.push(buildBlogTourUrl());
      } else if (stage === "community") {
        router.push(buildCommunityTourUrl());
      } else {
        stripQueryParam();
      }
    },
    [router, stage, stripQueryParam],
  );

  const { beat, onContinue, onExit, done, index } = useClientBuddyScript(
    shouldRun ? beats : [],
    {
      onComplete: () => {
        advanceStage();
      },
      onExit: () => {
        // Dismiss = end of tour, no surprise modals. We considered
        // escalating to the finale modal for late-stage dismissals
        // ("they've already gotten value, give them the CTA") but
        // popping a modal on top of an Esc press feels like a
        // dark-pattern. If the user wants the finale they can finish
        // the tour normally.
        emitTourTab(null);
        stripQueryParam();
      },
    },
  );

  useEffect(() => {
    if (!shouldRun || startedRef.current) return;
    startedRef.current = true;
    emitTourTab(stage ? STAGE_TAB[stage] : null);
  }, [shouldRun, stage]);

  useEffect(() => {
    if (!shouldRun || done) return;
    const fallback = stage ? STAGE_TAB[stage] : null;
    const tab = beat ? BEAT_TAB[beat.beatId] ?? fallback : fallback;
    emitTourTab(tab);
    // Re-emit shortly after to win any race against `<Feed>` initial
    // mount where the listener attaches a tick later than our first
    // dispatch. Cheap; no-ops if the listener has already applied.
    const t = window.setTimeout(() => emitTourTab(tab), 250);
    return () => window.clearTimeout(t);
  }, [beat, done, index, shouldRun, stage]);

  if (!shouldRun || done) return null;

  return (
    <BuddyOverlay beat={beat} onContinue={onContinue} onExit={onExit} />
  );
}
